import { useState } from 'react';
import { EditButton, Labeled, ShowContextProvider, ShowView, Tab, TabbedShowLayout, TextField, useDataProvider, useNotify, useRecordContext, useRefresh, useShowController } from 'react-admin';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContextTitle from '../../Components/ContextTitle';
import TypeNameToConfirmDialog from '../../Components/TypeNameToConfirmDialog';
import { deletePartnerConfig, errorMessage, PartnerRecord } from '../../lib/partners';

const DeactivatePartnerButton = () => {
  const record = useRecordContext<PartnerRecord>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!record || record.status !== 'active') return null;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await dataProvider.delete('partners', { id: record.id, previousData: record });
      notify(`Partner ${record.name} deactivated`, { type: 'success' });
      setOpen(false);
      refresh();
    } catch (error) {
      notify(errorMessage(error), { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button color="error" size="small" startIcon={<BlockIcon />} onClick={() => setOpen(true)}>
        Deactivate
      </Button>
      <TypeNameToConfirmDialog
        open={open}
        name={record.name}
        inputLabel="Partner name"
        title="Deactivate partner"
        content="The partner is marked as deactivated. Its companies and config are kept."
        confirmLabel="Deactivate"
        loading={loading}
        onConfirm={handleConfirm}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const PartnerConfigPanel = () => {
  const record = useRecordContext<PartnerRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!record) return null;
  const { config } = record;

  if (!config) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ mb: 2, color: 'text.secondary' }}>This partner has no config.</Box>
        <Button variant="contained" size="small" startIcon={<AddIcon />} component={Link} to={`/partners/${encodeURIComponent(record.uuid)}/config/create`}>
          Create config
        </Button>
      </Box>
    );
  }

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deletePartnerConfig(record.uuid);
      notify(`Config of ${record.name} deleted`, { type: 'success' });
      setOpen(false);
      refresh();
    } catch (error) {
      notify(errorMessage(error), { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Labeled>
        <TextField source="config.uuid" label="Config UUID" />
      </Labeled>
      <Labeled>
        <TextField source="config.domain" label="Domain" emptyText="—" />
      </Labeled>
      <Labeled>
        <TextField source="config.cssUrl" label="CSS URL" emptyText="—" />
      </Labeled>
      <Labeled>
        <TextField source="config.payrollUrl" label="Payroll URL" emptyText="—" />
      </Labeled>
      <Labeled>
        <TextField source="config.payrollUrlAuthStrategy" label="Payroll URL auth strategy" emptyText="—" />
      </Labeled>
      <Labeled label="Extra options">
        <Box component="pre" sx={{ m: 0, p: 1, bgcolor: 'action.hover', borderRadius: 1, overflowX: 'auto' }}>
          {config.extraOptions ? JSON.stringify(config.extraOptions, null, 2) : '—'}
        </Box>
      </Labeled>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" startIcon={<EditIcon />} component={Link} to={`/partners/${encodeURIComponent(record.uuid)}/config/edit`}>
          Edit config
        </Button>
        <Button color="error" size="small" startIcon={<DeleteIcon />} onClick={() => setOpen(true)}>
          Delete config
        </Button>
      </Box>
      <TypeNameToConfirmDialog open={open} name={record.name} inputLabel="Partner name" title="Delete partner config" content="The config is deleted permanently." confirmLabel="Delete config" loading={loading} onConfirm={handleDelete} onClose={() => setOpen(false)} />
    </Box>
  );
};

const PartnerShow = () => {
  const controllerProps = useShowController<PartnerRecord>();
  return (
    <ShowContextProvider value={controllerProps}>
      <ShowView title={<ContextTitle context="name" />}>
        <TabbedShowLayout>
          <Tab label="General">
            <TextField source="name" label="Name" />
            <TextField source="status" label="Status" />
            <TextField source="uuid" label="UUID" />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <EditButton />
              <DeactivatePartnerButton />
            </Box>
          </Tab>
          <Tab label="Config" path="config">
            <PartnerConfigPanel />
          </Tab>
        </TabbedShowLayout>
      </ShowView>
    </ShowContextProvider>
  );
};

export default PartnerShow;
